import { SESSION_OUTCOMES, SUCCESS_OUTCOMES, type OutcomeDefinition, type OutcomesView } from "@ti/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { BadgeCheck, CheckCircle2, MessagesSquare, ShieldAlert, Workflow } from "lucide-react";
import { useMemo } from "react";
import { z } from "zod";
import { OutcomeMixBar, PointsDelta, SuccessRate } from "@/components/domain/analytics";
import { StatTile } from "@/components/domain/stat";
import { ErrorPanel } from "@/components/layout/page";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import { Select } from "@/components/ui/input";
import { EmptyState, Skeleton } from "@/components/ui/misc";
import { OutcomeMixChart, SuccessRateChart } from "@/features/outcomes/charts";
import { SourceShare, VerificationGapCard, outcomeDefinitionColumns } from "@/features/outcomes/panels";
import { q } from "@/lib/api";
import { fmtCompact, fmtInt, fmtPct } from "@/lib/format";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/outcomes/")({
  validateSearch: z.object({ agentId: z.string().min(1).optional().catch(undefined) }),
  component: OutcomesPage,
});

function OutcomesPage() {
  const { agentId } = Route.useSearch();
  const navigate = Route.useNavigate();
  const view = useQuery(q.outcomes(agentId ? { agentId } : {}));
  const agents = useQuery(q.agents());
  const useCases = useQuery(q.useCases());
  const agentNames = useMemo(() => new Map(agents.data?.map((a) => [a.id, a.name])), [agents.data]);
  const ucNames = useMemo(() => new Map(useCases.data?.map((u) => [u.id, u.name])), [useCases.data]);
  const columns = useMemo(() => outcomeDefinitionColumns(agentNames, ucNames), [agentNames, ucNames]);
  const setAgent = (id: string | undefined) => void navigate({ search: { agentId: id }, replace: true, resetScroll: false });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-fg-3">Last 7 days{agentId ? ` · ${agentNames.get(agentId) ?? agentId}` : " · all agents"}</p>
        <Select aria-label="Agent" value={agentId ?? ""} onChange={(e) => setAgent(e.target.value || undefined)} className="w-56">
          <option value="">All agents</option>
          {agents.data?.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </Select>
      </div>
      {view.error ? (
        <ErrorPanel error={view.error} reset={() => void view.refetch()} />
      ) : !view.data ? (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-[104px]" />
            ))}
          </div>
          <Skeleton className="h-72" />
        </>
      ) : (
        <Content v={view.data} agentId={agentId} onAgent={setAgent} columns={columns} />
      )}
    </div>
  );
}

function Content({ v, agentId, onAgent, columns }: { v: OutcomesView; agentId?: string; onAgent: (id: string | undefined) => void; columns: ReturnType<typeof outcomeDefinitionColumns> }) {
  const sessions = SESSION_OUTCOMES.reduce((s, o) => s + v.totals[o], 0);
  const successes = SUCCESS_OUTCOMES.reduce((s, o) => s + v.totals[o], 0);
  const ground = v.bySource.event + v.bySource.business;
  const sourced = ground + v.bySource.inferred;
  if (!sessions)
    return (
      <Card>
        <EmptyState icon={<Workflow />} title="No sessions yet" description="Outcomes appear as sessions close and are enriched." />
      </Card>
    );
  return (
    <>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="Success rate · 7d" icon={<CheckCircle2 />} value={<SuccessRate value={v.successRate} />} sub={<><PointsDelta value={v.successRateDelta} /> vs prior 7d · {fmtCompact(successes)} successes</>} />
        <StatTile label="Sessions · 7d" icon={<MessagesSquare />} value={fmtInt(sessions)} sub={<OutcomeMixBar mix={v.totals} className="mt-1" />} />
        <StatTile label="Verification gap" icon={<ShieldAlert />} value={fmtPct(v.verificationGap.rate, 1)} sub={`${fmtInt(v.verificationGap.contradicted)} of ${fmtInt(v.verificationGap.claimedResolved)} claimed resolutions contradicted`} />
        <StatTile label="Ground truth" icon={<BadgeCheck />} value={sourced ? fmtPct(ground / sourced) : "—"} sub="of outcomes from events or business systems" />
      </div>

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <Card>
          <CardHeader title="Outcome mix" description="Share of sessions per day by outcome" />
          <CardBody>
            <OutcomeMixChart daily={v.daily} />
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Success rate" description="Resolved + converted, per day" />
          <CardBody>
            <SuccessRateChart daily={v.daily} height={244} />
          </CardBody>
        </Card>
      </div>

      <div className="grid items-start gap-3 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <Card>
          <CardHeader title="By agent" description="Select an agent to scope this page" />
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b text-left text-[11px] uppercase tracking-wide text-fg-3">
                <th scope="col" className="px-4 py-2 font-medium">
                  Agent
                </th>
                <th scope="col" className="px-2 py-2 text-right font-medium">
                  Sessions
                </th>
                <th scope="col" className="px-2 py-2 text-right font-medium">
                  Success
                </th>
                <th scope="col" className="px-2 py-2 text-right font-medium">
                  Δ 7d
                </th>
                <th scope="col" className="w-[30%] px-4 py-2 font-medium">
                  <span className="sr-only">Success rate bar</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {[...v.byAgent]
                .sort((a, b) => b.sessions - a.sessions)
                .map((a) => (
                  <tr
                    key={a.agentId}
                    tabIndex={0}
                    aria-current={agentId === a.agentId ? "true" : undefined}
                    onClick={() => onAgent(agentId === a.agentId ? undefined : a.agentId)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onAgent(agentId === a.agentId ? undefined : a.agentId);
                      }
                    }}
                    className={cn("cursor-pointer border-b last:border-0 hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-none", agentId === a.agentId && "bg-accent-soft/40")}
                  >
                    <td className="px-4 py-2 font-mono text-[12.5px] text-fg">{a.agentName}</td>
                    <td className="px-2 py-2 text-right tabular text-fg-2">{fmtInt(a.sessions)}</td>
                    <td className="px-2 py-2 text-right">
                      <SuccessRate value={a.successRate} />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <PointsDelta value={a.delta} />
                    </td>
                    <td className="px-4 py-2">
                      <div className="h-1.5 overflow-hidden rounded-sm bg-surface-3" role="meter" aria-valuenow={a.successRate} aria-valuemin={0} aria-valuemax={1} aria-label={`${a.agentName} success rate`}>
                        <div className="h-full rounded-sm" style={{ width: `${a.successRate * 100}%`, background: "var(--seq-400)" }} />
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </Card>
        <SourceShare bySource={v.bySource} />
      </div>

      <VerificationGapCard gap={v.verificationGap} />

      <Card>
        <CardHeader title="Outcome definitions" description="What success, failure and conversion mean, where each comes from, and the value attached" />
        <DataTable<OutcomeDefinition>
          data={v.definitions}
          columns={columns}
          dense
          stickyHeader={false}
          getRowId={(d) => d.id}
          initialSorting={[{ id: "sessions", desc: true }]}
          empty={{ icon: <Workflow />, title: "No outcome definitions", description: "Send outcomes with track() or connect a business system to define success." }}
        />
      </Card>
    </>
  );
}
